/// <amd-module name="TFS/Samples/Charts/ChartsSampleGenerator" />

import * as Resources from "TFS/Samples/Charts/Resources";
import * as Chart_Contracts from "VSS/Features/Charts/Contracts";
import { IVssPageContext } from "VSS/Platform/Context";

export class ChartSDKWidgetSettings {
    // Parses settings, or pre-populates common defaults for use by both config and widget
    public static parseOrDefault(settingsData: string): Chart_Contracts.ChartProps {
        if (settingsData != null && settingsData != "") {
            const chartProps = JSON.parse(settingsData) as Chart_Contracts.ChartProps;
            if (chartProps != null) {
                return chartProps;
            }
        }
        // If we failed to obtain a proper settings object, spin up with empty state as default (for now).
        return {} as Chart_Contracts.ChartProps;
    }

    /** Stringify an Options Object for run-time editing or presentation by human in client UI. */
    public static stringifyForEdit(options: Chart_Contracts.ChartProps): string {
        const serializableOptions: Chart_Contracts.ChartProps = {
            ...options,
            pageContext: undefined as any // Discard pageContext as irrelevant to configuring a chart.
        };
        return JSON.stringify(serializableOptions, null, "  ");
    }

    /** Stringify options string to prune out unneccessary whitespace.
     Note: A malformed options payload can cause Exceptions, which are responsibility of caller to handle.*/
    public static stringifyForStorage(options: string): string {
        const settingsObject = JSON.parse(options);
        return JSON.stringify(settingsObject);
    }
    public mode: ConfigMode;

    /* string representation of the options payload.*/
    public payload: string;
}

export enum ConfigMode {
    CommonChart_TCMJson,

    CommonChart_CFD,

    CommonChart_DemoPie,
    CommonChart_DemoBar,
    CommonChart_DemoStackedBar,
    CommonChart_DemoColumn,
    CommonChart_DemoHistogram,
    CommonChart_PivotTable,

    CommonChart_DemoLine,
    CommonChart_DemoArea,
    CommonChart_DemoStackedArea,

    CommonChart_DemoStackedColumn, // Used by Test Results Charting
    CommonChart_DemoHybrid, // Used by Test Results Charting
    CommonChart_DemoHtmlTable, // Used for screen reader support

    CommonChart_Scatter,
    CommonChart_AreaRange,
    CommonChart_Funnel,
    CommonChart_DemoGroupedStackedColumn, // Used by Velocity Widget
    CommonChart_DemoPlotBands, // Used for Weekends on Sprint Burndown Widget

    CommonChart_BubbleChart
}

export interface ConfigModeMetadata {
    name: string;
    value: ConfigMode;

    // Factory for generating option payloads, using the current widget size
    optionFactory: (pageContext: IVssPageContext) => Chart_Contracts.ChartProps; // Creates an options payload for instantiating a chart
}

export class SDKWidgetContentProvider {
    /** Construct an array identified by the different modes with supplemental metadata*/
    public static getModeMetadata(): ConfigModeMetadata[] {
        return [
            {
                name: Resources.ChartSample_CFD,
                value: ConfigMode.CommonChart_CFD,
                optionFactory: ChartOptionsFactories.generateCfdAsComboOptions
            },
            {
                name: Resources.ChartSample_TCM,
                value: ConfigMode.CommonChart_TCMJson,
                optionFactory: ChartOptionsFactories.generateTestDurationsAsComboOptions
            },
            {
                name: Resources.ChartSample_Pie,
                value: ConfigMode.CommonChart_DemoPie,
                optionFactory: ChartOptionsFactories.generateDemoPie
            },
            {
                name: Resources.ChartSample_Bar,
                value: ConfigMode.CommonChart_DemoBar,
                optionFactory: ChartOptionsFactories.generateDemoBar
            },
            {
                name: Resources.ChartSample_StackedBar,
                value: ConfigMode.CommonChart_DemoStackedBar,
                optionFactory: ChartOptionsFactories.generateDemoStackedBar
            },
            {
                name: Resources.ChartSample_Column,
                value: ConfigMode.CommonChart_DemoColumn,
                optionFactory: ChartOptionsFactories.generateDemoColumn
            },
            {
                name: Resources.ChartSample_StackedColumn,
                value: ConfigMode.CommonChart_DemoStackedColumn,
                optionFactory: ChartOptionsFactories.generateDemoStackedColumn
            },
            {
                name: Resources.ChartSample_Histogram,
                value: ConfigMode.CommonChart_DemoHistogram,
                optionFactory: ChartOptionsFactories.generateDemoHistogram
            },
            {
                name: Resources.ChartSample_PivotTable,
                value: ConfigMode.CommonChart_PivotTable,
                optionFactory: ChartOptionsFactories.generateDemoPivotTable
            },
            {
                name: Resources.ChartSample_Line,
                value: ConfigMode.CommonChart_DemoLine,
                optionFactory: ChartOptionsFactories.generateDemoLine
            },
            {
                name: Resources.ChartSample_Area,
                value: ConfigMode.CommonChart_DemoArea,
                optionFactory: ChartOptionsFactories.generateDemoArea
            },
            {
                name: Resources.ChartSample_StackedArea,
                value: ConfigMode.CommonChart_DemoStackedArea,
                optionFactory: ChartOptionsFactories.generateDemoStackedArea
            },
            {
                name: Resources.ChartSample_Scatter,
                value: ConfigMode.CommonChart_Scatter,
                optionFactory: ChartOptionsFactories.generateDemoScatter
            },
            {
                name: Resources.ChartSample_AreaRange,
                value: ConfigMode.CommonChart_AreaRange,
                optionFactory: ChartOptionsFactories.generateAreaRange
            },
            {
                name: Resources.ChartSample_Hybrid,
                value: ConfigMode.CommonChart_DemoHybrid,
                optionFactory: ChartOptionsFactories.generateHybridChart
            },
            {
                name: Resources.ChartSample_StackedColumn_W_Grouping,
                value: ConfigMode.CommonChart_DemoGroupedStackedColumn,
                optionFactory: ChartOptionsFactories.generateDemoGroupedStackedColumn
            },
            {
                name: Resources.ChartSample_PlotBandsNLines,
                value: ConfigMode.CommonChart_DemoPlotBands,
                optionFactory: ChartOptionsFactories.generatePlotBandsAndLines
            },
            {
                name: Resources.ChartSample_Funnel,
                value: ConfigMode.CommonChart_Funnel,
                optionFactory: ChartOptionsFactories.generateDemoFunnel
            },
            {
                name: Resources.ChartSample_Bubble,
                value: ConfigMode.CommonChart_BubbleChart,
                optionFactory: ChartOptionsFactories.generateDemoBubble
            }
        ];
    }

    public static findModeMetadata(configMode: ConfigMode): ConfigModeMetadata {
        const modeOptions = SDKWidgetContentProvider.getModeMetadata();
        const matches = modeOptions.filter((element: ConfigModeMetadata, idx: number) => {
            return element.value == configMode;
        });
        if (matches.length == 0) {
            throw new Error("No match could be found for the requested config metadata");
        }
        return matches[0];
    }
}

export class ChartOptionsFactories {
    private static makeRandomNumberArray(length: number, baseline: number = 0): number[] {
        const elements = [];
        for (let i = 0; i < length; i++) {
            elements.push(Math.round(Math.random() * (i + 3)) + baseline);
        }
        return elements;
    }

    private static makeDateArray(length: number): string[] {
        const elements = [];
        for (let i = 0; i < length; i++) {
            const date = "2019-" + "02-" + (i + 1); // YYYY-MM-DD format
            elements.push(date);
        }
        return elements;
    }

    private static makeFruitArray(): string[] {
        return ["Pineapple", "Orange", "Banana", "Coconut"];
    }
    /**Ensure:
        1-control options object contains chart host options with sizing settings.
        2-Animation behavior is suppressed, when *re-configuring* a chart.
        Allow pre-existing hard-coded settings to pass through. */
    public static applyHostOptions(chartOptions: Chart_Contracts.ChartProps, isFirstLoad: boolean): Chart_Contracts.ChartProps {
        chartOptions.click = (click: Chart_Contracts.ClickEvent) => {
            alert(
                "A click was detected element on this chart." +
                    "\n Label: " +
                    click.labelName +
                    "\n Series: " +
                    click.seriesName +
                    "\n Position in series: " +
                    click.itemX +
                    "\n Item name: " +
                    click.itemName +
                    "\n Value: " +
                    click.itemY
            );
        };

        if (!isFirstLoad) {
            chartOptions.suppressAnimation = true;
        }

        return chartOptions;
    }

    /** Snapshot from existing CFD chart options.*/
    public static getCfdSamples(): any[] {
        return [
            // Note: Traditional serialized CFD payload uses 5K to serialize to widget settings. Trimming Ready and PR sections to economize.

            { date: "2016-02-22T07:59:59.000Z", name: "Design", value: 0 },
            { date: "2016-02-24T07:59:59.000Z", name: "Design", value: 0 },
            { date: "2016-02-26T07:59:59.000Z", name: "Design", value: 5 },
            { date: "2016-02-29T07:59:59.000Z", name: "Design", value: 6 },
            { date: "2016-03-02T07:59:59.000Z", name: "Design", value: 1 },
            { date: "2016-03-04T07:59:59.000Z", name: "Design", value: 0 },
            { date: "2016-03-07T07:59:59.000Z", name: "Design", value: 2 },
            { date: "2016-03-09T07:59:59.000Z", name: "Design", value: 1 },
            { date: "2016-03-11T07:59:59.000Z", name: "Design", value: 1 },
            { date: "2016-02-22T07:59:59.000Z", name: "Development", value: 0 },
            { date: "2016-02-24T07:59:59.000Z", name: "Development", value: 0 },
            { date: "2016-02-26T07:59:59.000Z", name: "Development", value: 4 },
            { date: "2016-02-29T07:59:59.000Z", name: "Development", value: 4 },
            { date: "2016-03-02T07:59:59.000Z", name: "Development", value: 4 },
            { date: "2016-03-04T07:59:59.000Z", name: "Development", value: 1 },
            { date: "2016-03-07T07:59:59.000Z", name: "Development", value: 1 },
            { date: "2016-03-09T07:59:59.000Z", name: "Development", value: 1 },
            { date: "2016-03-11T07:59:59.000Z", name: "Development", value: 1 },
            { date: "2016-02-22T07:59:59.000Z", name: "Verification", value: 0 },
            { date: "2016-02-24T07:59:59.000Z", name: "Verification", value: 0 },
            { date: "2016-02-26T07:59:59.000Z", name: "Verification", value: 2 },
            { date: "2016-02-29T07:59:59.000Z", name: "Verification", value: 2 },
            { date: "2016-03-02T07:59:59.000Z", name: "Verification", value: 1 },
            { date: "2016-03-04T07:59:59.000Z", name: "Verification", value: 5 },
            { date: "2016-03-07T07:59:59.000Z", name: "Verification", value: 2 },
            { date: "2016-03-09T07:59:59.000Z", name: "Verification", value: 3 },
            { date: "2016-03-11T07:59:59.000Z", name: "Verification", value: 5 },
            { date: "2016-02-22T07:59:59.000Z", name: "Ready to Deploy", value: 0 },
            { date: "2016-02-24T07:59:59.000Z", name: "Ready to Deploy", value: 0 },
            { date: "2016-02-26T07:59:59.000Z", name: "Ready to Deploy", value: 0 },
            { date: "2016-02-29T07:59:59.000Z", name: "Ready to Deploy", value: 0 },
            { date: "2016-03-02T07:59:59.000Z", name: "Ready to Deploy", value: 1 },
            { date: "2016-03-04T07:59:59.000Z", name: "Ready to Deploy", value: 1 },
            { date: "2016-03-07T07:59:59.000Z", name: "Ready to Deploy", value: 3 },
            { date: "2016-03-09T07:59:59.000Z", name: "Ready to Deploy", value: 4 },
            { date: "2016-03-11T07:59:59.000Z", name: "Ready to Deploy", value: 4 },
            { date: "2016-02-22T07:59:59.000Z", name: "Done", value: 0 },
            { date: "2016-02-24T07:59:59.000Z", name: "Done", value: 0 },
            { date: "2016-02-26T07:59:59.000Z", name: "Done", value: 1 },
            { date: "2016-02-29T07:59:59.000Z", name: "Done", value: 1 },
            { date: "2016-03-02T07:59:59.000Z", name: "Done", value: 13 },
            { date: "2016-03-04T07:59:59.000Z", name: "Done", value: 14 },
            { date: "2016-03-07T07:59:59.000Z", name: "Done", value: 15 },
            { date: "2016-03-09T07:59:59.000Z", name: "Done", value: 15 },
            { date: "2016-03-11T07:59:59.000Z", name: "Done", value: 17 }
        ];
    }

    /*This is a non-native lazy data conversion from purpose specific CFD render format, to a general trend form. */
    public static generateCfdAsComboOptions(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        const data = ChartOptionsFactories.getCfdSamples();
        const palette = ["#9CC3B2", "#339947", "#207752", "#BFD8CD", "#00643A", "#7CAF9A", "#8DC54B", "#56987D", "#60AF49"];

        // Here, we take a 1D tuple data set, and express it as nested array of series elements
        const series = [];
        let row = {} as Chart_Contracts.DataSeries;
        const xAxis = {
            labelValues: [],
            title: "",
            labelFormatMode: Chart_Contracts.LabelFormatModes.DateTime_DayInMonth,
            formatAccessibleLabelsUsingAxisCategories: true
        } as Chart_Contracts.AxisOptions;

        // Now the re-packing of data happens. In this case, we know array layout, which bypasses some logic to safely determine identify bounds.
        let j = 0;
        for (let i = 0; i < data.length; i++) {
            // Extract distinct dates for the label values
            if (i < 9) {
                const date = new Date(data[i].date);
                (xAxis as any).labelValues[i] = date;
            }
            // Create a new series with each distinct set of values.
            if (i % 9 == 0) {
                j++;
                row = {} as Chart_Contracts.DataSeries;
                row.name = data[i].name;
                row.data = [];
                row.color = palette[j % 9];
                series.push(row);
            }
            const value: number = data[i].value;
            row.data.push(value);
        }

        return {
            pageContext: pageContext,
            title: Resources.ChartSample_CFD,
            chartType: Chart_Contracts.ChartTypesConstants.StackedArea,
            legend: {
                enabled: false // Override default stacked presentation, by hiding legend.
            },
            tooltip: {
                onlyShowFocusedSeries: true
            },
            xAxis: xAxis,
            yAxis: {
                title: "Backlog items count"
            } as Chart_Contracts.AxisOptions,
            series: series.reverse() // Reverse the order - Build the series from the bottom up.
        };
    }

    /*This chart is different, in that it uses both a bar chart, and a line chart. */
    public static generateTestDurationsAsComboOptions(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        const series = [];

        const durationTitle = "Duration (ms)";
        const totalTitle = "Total tests";

        // Note: The line chart is supplied *after* content which renders on the secondary Axis. This is significant, because it affects render order.
        // It looks very awkward to have line chart content overlaid by column chart content.
        const durationRow = {
            name: durationTitle,
            data: [1938443, 656, 1623919, 576, 576, 720, 593, 626, 810, 686],
            color: "#5DA5DA"
        } as Chart_Contracts.DataSeries;
        series.push(durationRow);

        const testCountRow = {
            name: totalTitle,
            data: [513, 11, 524, 11, 11, 11, 11, 11, 11, 11],
            color: "#B276B2",
            useSecondaryAxis: true
        } as Chart_Contracts.DataSeries;
        series.push(testCountRow);

        return {
            pageContext: pageContext,
            title: Resources.ChartSample_TCM,
            chartType: Chart_Contracts.ChartTypesConstants.ColumnLine,
            xAxis: {
                labelValues: [
                    "VSO.TRA.Master.CI_20160309.3",
                    "VSO.TRA.Master.CI_20160309.4",
                    "VSO.TRA.Master.CI_20160309.5",
                    "VSO.TRA.Master.CI_20160309.6",
                    "VSO.TRA.Master.CI_20160309.7",
                    "VSO.TRA.Master.CI_20160309.8",
                    "VSO.TRA.Master.CI_20160309.9",
                    "VSO.TRA.Master.CI_20160309.10",
                    "VSO.TRA.Master.CI_20160309.11",
                    "VSO.TRA.Master.CI_20160309.12"
                ],
                labelsEnabled: false
            } as Chart_Contracts.AxisOptions,
            yAxis: {
                title: durationTitle
            } as Chart_Contracts.AxisOptions,
            yAxisSecondary: {
                title: totalTitle,
                min: 0 // This is needed on secondary axis, otherwise, the chart will use the negative value of max value.
            } as Chart_Contracts.AxisOptions,
            series: series
        };
    }

    public static generateDemoPie(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        return {
            pageContext: pageContext,
            title: Resources.ChartSample_Pie,
            chartType: Chart_Contracts.ChartTypesConstants.Pie,
            specializedOptions: {
                showLabels: true,
                size: 200 as any,
                center: [null, 90]
            } as any,
            series: [{ data: [4, 3, 2, 1, 123, 12, 53, 23, 12] }],
            xAxis: {
                labelValues: [
                    "Pineapple",
                    "Orange you excited to use charts?",
                    "Mushy banana",

                    "A mighty coconut",
                    "A modest plum",
                    "A grand tomato",

                    "A friendly banana",
                    "A luminous strawberry",
                    "A delicious peach"
                ]
            },
            legend: {
                limitLabelSize: true
            }
        };
    }

    public static generateDemoBar(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        return {
            pageContext: pageContext,
            title: Resources.ChartSample_Bar,
            chartType: Chart_Contracts.ChartTypesConstants.Bar,
            series: [
                {
                    name: "Fruit Eaten Today",
                    data: ChartOptionsFactories.makeRandomNumberArray(4)
                }
            ],
            xAxis: {
                labelValues: ChartOptionsFactories.makeFruitArray()
            }
        };
    }

    public static generateDemoStackedBar(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        return {
            pageContext: pageContext,
            title: Resources.ChartSample_StackedBar,
            chartType: Chart_Contracts.ChartTypesConstants.StackedBar,
            series: [
                {
                    name: "Steve",
                    data: ChartOptionsFactories.makeRandomNumberArray(4)
                },
                {
                    name: "Jane",
                    data: ChartOptionsFactories.makeRandomNumberArray(4)
                },
                {
                    name: "Aaron",
                    data: ChartOptionsFactories.makeRandomNumberArray(4)
                }
            ],
            xAxis: {
                labelValues: ChartOptionsFactories.makeFruitArray()
            }
        };
    }

    public static generateDemoColumn(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        return {
            pageContext: pageContext,
            title: Resources.ChartSample_Column,
            chartType: Chart_Contracts.ChartTypesConstants.Column,
            series: [
                {
                    name: "Fruit Eaten Today",
                    data: ChartOptionsFactories.makeRandomNumberArray(4)
                }
            ],
            xAxis: {
                labelValues: ChartOptionsFactories.makeFruitArray()
            }
        };
    }

    public static generateDemoStackedColumn(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        return {
            pageContext: pageContext,
            title: Resources.ChartSample_StackedColumn,
            chartType: Chart_Contracts.ChartTypesConstants.StackedColumn,
            series: [
                {
                    name: "Steve",
                    data: ChartOptionsFactories.makeRandomNumberArray(4)
                },
                {
                    name: "Jane",
                    data: ChartOptionsFactories.makeRandomNumberArray(4)
                },
                {
                    name: "Aaron",
                    data: ChartOptionsFactories.makeRandomNumberArray(4)
                }
            ],
            xAxis: {
                labelValues: ChartOptionsFactories.makeFruitArray()
            }
        };
    }

    public static generateDemoGroupedStackedColumn(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        const stackedColumn = ChartOptionsFactories.generateDemoStackedColumn(pageContext);
        stackedColumn.series[0].stackGroup = "Left";
        stackedColumn.series[1].stackGroup = "Right";
        stackedColumn.series[2].stackGroup = "Right";
        return stackedColumn;
    }

    public static generatePlotBandsAndLines(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        const trendOptions = ChartOptionsFactories.generateCfdAsComboOptions(pageContext);
        const xAxis = trendOptions.xAxis!;
        xAxis.plotBands = [
            {
                color: "yellow",
                start: 1,
                end: 1.4
            },
            {
                color: "yellow",
                start: 3,
                end: 3.4
            },
            {
                color: "yellow",
                start: 5,
                end: 5.4
            }
        ];
        xAxis.plotLines = [
            {
                axisPosition: 4,
                labelText: "A most auspicious data point!"
            }
        ];
        return trendOptions;
    }

    public static generateDemoLine(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        const options: Chart_Contracts.ChartProps = {
            pageContext: pageContext,
            title: Resources.ChartSample_Line,
            chartType: Chart_Contracts.ChartTypesConstants.Line,
            series: [],
            xAxis: {
                labelFormatMode: Chart_Contracts.LabelFormatModes.DateTime_DayInMonth,
                labelValues: ChartOptionsFactories.makeDateArray(10),
                formatAccessibleLabelsUsingAxisCategories: true
            }
        };

        const names = ChartOptionsFactories.makeFruitArray();
        for (let i = 0; i < names.length; i++) {
            options.series.push({
                name: names[i],
                data: ChartOptionsFactories.makeRandomNumberArray(10, i)
            });
        }
        return options;
    }

    public static generateDemoArea(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        return {
            pageContext: pageContext,
            title: Resources.ChartSample_Area,
            chartType: Chart_Contracts.ChartTypesConstants.Area,
            series: [
                {
                    name: "Banana Consumption",
                    data: ChartOptionsFactories.makeRandomNumberArray(10)
                }
            ],
            xAxis: {
                labelFormatMode: Chart_Contracts.LabelFormatModes.DateTime_DayInMonth,
                labelValues: ChartOptionsFactories.makeDateArray(10),
                formatAccessibleLabelsUsingAxisCategories: true
            }
        };
    }

    public static generateDemoStackedArea(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        const options: Chart_Contracts.ChartProps = {
            pageContext: pageContext,
            title: Resources.ChartSample_StackedArea,
            chartType: Chart_Contracts.ChartTypesConstants.StackedArea,
            series: [],
            xAxis: {
                labelFormatMode: Chart_Contracts.LabelFormatModes.DateTime_DayInMonth,
                labelValues: ChartOptionsFactories.makeDateArray(10),
                formatAccessibleLabelsUsingAxisCategories: true
            }
        };

        const names = ChartOptionsFactories.makeFruitArray();
        for (let i = 0; i < names.length; i++) {
            options.series.push({
                name: names[i],
                data: ChartOptionsFactories.makeRandomNumberArray(10)
            });
        }

        return options;
    }

    public static generateHybridChart(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        const options: Chart_Contracts.ChartProps = {
            pageContext: pageContext,
            title: Resources.ChartSample_Hybrid,
            chartType: Chart_Contracts.ChartTypesConstants.Hybrid,
            series: [],
            xAxis: {
                labelFormatMode: Chart_Contracts.LabelFormatModes.DateTime_DayInMonth,
                labelValues: ChartOptionsFactories.makeDateArray(10),
                formatAccessibleLabelsUsingAxisCategories: true
            },
            yAxis: {
                title: "Line & area Plot"
            },
            yAxisSecondary: {
                title: "Bar Plots",
                min: 0
            },
            specializedOptions: {
                chartTypes: [
                    Chart_Contracts.ChartTypesConstants.Area,
                    Chart_Contracts.ChartTypesConstants.Line,
                    Chart_Contracts.ChartTypesConstants.Column,
                    Chart_Contracts.ChartTypesConstants.Column,
                    Chart_Contracts.ChartTypesConstants.AreaRange,
                    Chart_Contracts.ChartTypesConstants.Scatter
                ],
                includeMarkers: true
            }
        };

        const names = ChartOptionsFactories.makeFruitArray();
        for (let i = 0; i < 4; i++) {
            options.series.push({
                name: names[i],
                data: ChartOptionsFactories.makeRandomNumberArray(10)
            });
        }

        options.series.push({
            name: "AreaRange",
            data: ChartOptionsFactories.generateRangeData()
        });
        options.series.push({
            name: "Scatter",
            color: "#F00",
            data: [[5, 3.8], [2, 6.7], [1, 8.7], [4, 2.5], [2, 5.5], [3, 6.2], [2, 5.7], [2, 4.6], [5, 2.8], [2, 4.7]]
        });

        // Apply special settings to the last two series
        options.series[2].color = "green";
        options.series[2].useSecondaryAxis = true;
        options.series[3].color = "red";
        options.series[3].useSecondaryAxis = true;

        return options;
    }

    public static generateDemoHistogram(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        const series = [];
        const columnValues = {
            data: [
                43,
                { y: 30, color: "green" },
                { y: 28, color: "green" },
                { y: 40, color: "orange", name: "Bad build #1" },
                { y: 15, color: "orange", name: "Bad build #2" },
                { y: 22, color: "green" },
                { y: 11, color: "red" },
                { y: 41, color: "green" },
                { y: 25, color: "green" },
                { y: 40, color: "green" }
            ],
            name: "Test Durations (m)"
        } as Chart_Contracts.DataSeries;
        series.push(columnValues);

        return {
            pageContext: pageContext,
            title: Resources.ChartSample_Histogram,
            chartType: Chart_Contracts.ChartTypesConstants.Histogram,
            xAxis: {
                labelsEnabled: false,
                markingsEnabled: false
            } as Chart_Contracts.AxisOptions,
            yAxis: {
                labelsEnabled: false,
                markingsEnabled: false
            } as Chart_Contracts.AxisOptions,
            tooltip: { enabled: false },
            series: series
        };
    }

    public static generateDemoPivotTable(): Chart_Contracts.ChartProps {
        const data = [
            {
                name: "Pineapple",
                data: [
                    [0, 0, 26],
                    [0, 1, 2],
                    [0, 2, 26],
                    [0, 3, 2],
                    [1, 0, 8],
                    [1, 1, 0],
                    [1, 2, 2],
                    [1, 3, 1],
                    [2, 0, 0],
                    [2, 1, 1],
                    [2, 2, 2],
                    [2, 3, 1]
                ] as any
            }
        ] as Chart_Contracts.DataSeries[];

        return {
            chartType: Chart_Contracts.ChartTypesConstants.Table,
            xAxis: {
                labelValues: ["Pineapple", "Orange", "Coconuts", "Total"]
            } as Chart_Contracts.AxisOptions,
            yAxis: {
                labelValues: ["P0", "P1", "P2", "Total"]
            } as Chart_Contracts.AxisOptions,
            series: [
                {
                    name: "Pineapple",
                    data: [[0, 0, 1], [0, 1, 2], [0, 2, 3]] as any
                },
                {
                    name: "Orange",
                    data: [[1, 0, 4.0203], [1, 1, 5], [1, 2, 6]] as any
                },
                {
                    name: "Coconuts",
                    data: [[2, 0, 7], [2, 1, 8], [2, 2, 9]] as any
                },
                {
                    name: "Total",
                    data: [[3, 0, 12.0400001], [3, 1, 15], [3, 2, 18], [0, 3, 6], [1, 3, 15], [2, 3, 24], [3, 3, 10]] as any,
                    color: Chart_Contracts.ChartColorizer.Transparent
                }
            ]
        } as Chart_Contracts.ChartProps;
    }

    public static generateDemoScatter(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        const series = [];
        const data1 = {
            name: "pineapple",
            color: "#F58B1F",
            data: [
                [1, 5.1],
                [3, 6.6],
                [4, 8.7],
                [7, 9.2],
                [5, 4.7],
                [5, 3.1],
                [7, 9.2],
                [6, 4.3],
                [1, 2.1],
                [3, 3.6],
                [2, 8.1],
                [7, 1.2],
                [6, 2.7],
                [5, 3.4],
                [8, 9.5],
                [6, 5.3]
            ]
        } as Chart_Contracts.DataSeries;
        const data2 = {
            name: "apple",
            color: "#F00",
            data: [
                [5, 3.8],
                [2, 6.7],
                [1, 8.7],
                [4, 2.5],
                [2, 5.5],
                [3, 6.2],
                [2, 5.7],
                [2, 4.6],
                [5, 2.8],
                [2, 4.7],
                [2, 5.7],
                [1, 2.5],
                [0, 5.6],
                [2, 5.2],
                [5, 2.7],
                [9, 3.6]
            ]
        } as Chart_Contracts.DataSeries;
        series.push(data1);
        series.push(data2);

        const options: Chart_Contracts.ChartProps = {
            pageContext: pageContext,
            title: Resources.ChartSample_Scatter,
            chartType: Chart_Contracts.ChartTypesConstants.Scatter,
            series: series
        };

        return options;
    }

    public static generateDemoBubble(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        return {
            pageContext: pageContext,
            title: Resources.ChartSample_Bubble,
            chartType: Chart_Contracts.ChartTypesConstants.Bubble,
            xAxis: {
                labelFormatMode: Chart_Contracts.LabelFormatModes.DateTime_DayInMonth,
                labelValues: [
                    "2016-02-20",
                    "2016-02-21",
                    "2016-02-22",
                    "2016-02-23",
                    "2016-02-24",
                    "2016-02-25",
                    "2016-02-26",
                    "2016-02-27",
                    "2016-02-28"
                ],
                title: "Date",
                formatAccessibleLabelsUsingAxisCategories: true
            },
            yAxis: {
                labelFormatMode: Chart_Contracts.LabelFormatModes.Textual,
                title: "Task Name",
                labelValues: ["l0 Tests", "l1 Tests", "msbuild", "publish results"],
                min: 0,
                max: 3
            },
            specializedOptions: {
                measureName: "Failure count"
            },
            series: [
                {
                    data: [{ x: 0, y: 0, z: 10 }, { x: 1, y: 0, z: 30 }, { x: 2, y: 0, z: 100 }, { x: 5, y: 0, z: 1 }]
                },
                {
                    data: [{ x: 1, y: 1, z: 2 }, { x: 2, y: 1, z: 3 }, { x: 3, y: 1, z: 40 }, { x: 4, y: 1, z: 50 }]
                },
                {
                    data: [{ x: 2, y: 2, z: 5 }, { x: 3, y: 2, z: 70 }, { x: 4, y: 2, z: 80 }, { x: 5, y: 2, z: 10 }]
                },
                {
                    data: [{ x: 3, y: 3, z: 5 }, { x: 4, y: 3, z: 70 }, { x: 5, y: 3, z: 80 }, { x: 6, y: 3, z: 10 }]
                }
            ]
        };
    }

    public static generateAreaRange(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        // Date from 1/1/2016 ~ 1/10/2016 in UTC format
        const options: Chart_Contracts.ChartProps = {
            pageContext: pageContext,
            title: Resources.ChartSample_AreaRange,
            chartType: Chart_Contracts.ChartTypesConstants.AreaRange,
            xAxis: {
                labelFormatMode: Chart_Contracts.LabelFormatModes.DateTime_DayInMonth,
                labelValues: ChartOptionsFactories.makeDateArray(10),
                formatAccessibleLabelsUsingAxisCategories: true
            },
            series: [
                {
                    name: "Range",
                    data: ChartOptionsFactories.generateRangeData(),
                    color: "#D0D0D0"
                }
            ],
            legend: {
                enabled: false
            }
        };

        return options;
    }

    public static generateRangeData(): [number, number][] {
        return [
            [14.3, 27.7],
            [14.5, 27.8],
            [15.5, 29.6],
            [16.7, 30.7],
            [16.5, 25.0],
            [17.8, 25.7],
            [13.5, 24.8],
            [10.5, 21.4],
            [9.2, 23.8],
            [11.6, 21.8]
        ];
    }

    public static generateDemoFunnel(pageContext: IVssPageContext): Chart_Contracts.ChartProps {
        const data: Chart_Contracts.Datum[] = [20, 12, 5, 1] as Chart_Contracts.Datum[];

        const options: Chart_Contracts.ChartProps = {
            pageContext: pageContext,
            title: Resources.ChartSample_Funnel,
            chartType: Chart_Contracts.ChartTypesConstants.Funnel,
            xAxis: {
                labelFormatMode: Chart_Contracts.LabelFormatModes.Textual,
                labelValues: ["Cherry", "Pineapple", "Apple", "Banana"]
            },
            series: [
                {
                    name: "Funnel Data",
                    data: data
                }
            ],
            legend: {
                enabled: false
            },
            specializedOptions: {
                neckHeight: "10%",
                neckWidth: "15%",
                height: "50%",
                width: "50%"
            } as Chart_Contracts.FunnelChartOptions
        };

        return options;
    }
}
